import React from "react";
import { shareService } from "../services/shareService";

const STORAGE_KEY = "cart";

const CartContext = React.createContext({
  items: [],
  addItem: () => {},
  removeItem: () => {},
  contains: () => false,
  containsAll: () => false,
  clearCart: () => {},
  shareCart: () => Promise.resolve(),
  itemCount: 0,
});
CartContext.displayName = "CartContext";

function readCartItems() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  // Migrate old pipe-delimited ID format — full item objects can't be recovered
  if (!raw.startsWith("[")) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCartItems(items) {
  if (items.length === 0) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }
}

function CartProvider({ children }) {
  const [items, setItems] = React.useState(readCartItems);
  const cartIds = items.map((i) => String(i.program_site_id || i.id));

  // Sync to localStorage after every change — avoids side effects inside state updaters
  React.useEffect(() => {
    writeCartItems(items);
  }, [items]);

  const addItem = React.useCallback(
    (item) => {
      const id = String(item.program_site_id || item.id);
      setItems((prev) => {
        if (prev.some((i) => String(i.program_site_id || i.id) === id)) return prev;
        return [...prev, item];
      });
    },
    []
  );

  const removeItem = React.useCallback(
    (item) => {
      const id = String(item.program_site_id || item.id);
      setItems((prev) => prev.filter((i) => String(i.program_site_id || i.id) !== id));
    },
    []
  );

  const contains = React.useCallback(
    (item) => {
      const id = String(item.program_site_id || item.id);
      return cartIds.includes(id);
    },
    [cartIds]
  );

  const containsAll = React.useCallback(
    (models) => {
      const ids = models.map((m) => String(m.program_site_id || m.id));
      return ids.every((id) => cartIds.includes(id));
    },
    [cartIds]
  );

  const clearCart = React.useCallback(() => {
    setItems([]);
  }, []);

  const shareCart = React.useCallback(
    ({ email, client, comments }) => {
      return shareService.shareCart({
        email,
        client,
        comments,
        agencies: items,
        current_url: window.location.href,
      });
    },
    [items]
  );

  const value = React.useMemo(
    () => ({
      items,
      cartIds,
      addItem,
      removeItem,
      contains,
      containsAll,
      clearCart,
      shareCart,
      itemCount: cartIds.length,
    }),
    [items, cartIds, addItem, removeItem, contains, containsAll, clearCart, shareCart]
  );
  // cartIds is derived from items so it updates in sync — no stale closure risk

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

function useCart() {
  return React.useContext(CartContext);
}

export { CartContext, CartProvider, useCart };
